import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Smartphone, QrCode, Apple, Download, CheckCircle } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const FEATURES = [
  "Browse & book luxury events",
  "Talk to ARIA, your AI concierge",
  "Access VIP galleries & media",
  "Real-time event notifications",
  "AI Photo Booth & Poster Creator",
  "View tickets & check in via QR",
];

export default function DownloadPage() {
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(ua));
    setIsAndroid(/android/.test(ua));
  }, []);

  return (
    <div data-testid="download-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="text-center mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-4">Mobile App</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl font-light text-white mb-4">
              Get <span className="gold-gradient-text">Aristocrats JA</span><br />On Your Phone
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body text-base max-w-lg mx-auto">
              Install our app directly to your home screen. No app store needed — works on both iPhone and Android.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* QR Code */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="glass-card p-10 text-center">
              <QrCode size={28} className="text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-3">Scan to Install</h3>
              <p className="text-white/40 font-body text-xs mb-6">Point your phone camera at the QR code</p>
              <div className="bg-white p-4 inline-block mx-auto mb-4">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/download-qr`} alt="Download QR" className="w-48 h-48" data-testid="download-qr" />
              </div>
              <p className="text-white/30 font-body text-[10px]">Opens the web app — then follow install instructions below</p>
            </motion.div>

            {/* Instructions */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={4} className="glass-card p-10">
              <Smartphone size={28} className="text-gold mb-4" />
              <h3 className="font-heading text-xl text-white mb-6">Install Instructions</h3>

              <div className="space-y-6">
                {/* iPhone */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Apple size={16} className="text-white/60" />
                    <h4 className="text-white font-body text-sm font-medium">iPhone / iPad</h4>
                  </div>
                  <ol className="space-y-2 text-white/50 font-body text-xs pl-4">
                    <li>1. Open this site in <span className="text-gold">Safari</span></li>
                    <li>2. Tap the <span className="text-gold">Share</span> button (square with arrow)</li>
                    <li>3. Scroll down and tap <span className="text-gold">"Add to Home Screen"</span></li>
                    <li>4. Tap <span className="text-gold">"Add"</span> — Done!</li>
                  </ol>
                </div>

                {/* Android */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Download size={16} className="text-white/60" />
                    <h4 className="text-white font-body text-sm font-medium">Android</h4>
                  </div>
                  <ol className="space-y-2 text-white/50 font-body text-xs pl-4">
                    <li>1. Open this site in <span className="text-gold">Chrome</span></li>
                    <li>2. Tap the <span className="text-gold">three dots</span> menu (top right)</li>
                    <li>3. Tap <span className="text-gold">"Add to Home screen"</span> or <span className="text-gold">"Install app"</span></li>
                    <li>4. Tap <span className="text-gold">"Install"</span> — Done!</li>
                  </ol>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Features */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={5} className="glass-card p-10">
            <h3 className="font-heading text-xl text-white text-center mb-8">What You Get</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {FEATURES.map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <CheckCircle size={16} className="text-gold flex-shrink-0" />
                  <span className="text-white/60 font-body text-sm">{f}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
